<script setup lang="ts">
import { computed } from 'vue';
import gettext from '@/i18n/gettext';
import { resources, ResourceTab } from '@/models/Resources';
import useAuthStore from '@/store/AuthStore';

const authStore = useAuthStore();
const { $gettext } = gettext;

const technicalDataPdfLink = computed(() => {
  if (authStore.language === 'de') {
    return (
      resources[ResourceTab.TECHNICAL_DATA].de.find(
        (entry: { title: string; href: string }) =>
          entry.title === 'Technische Daten',
      )?.href || ''
    );
  }
  if (authStore.language === 'en') {
    return (
      resources[ResourceTab.TECHNICAL_DATA].en.find(
        (entry: { title: string; href: string }) =>
          entry.title === 'Technical Data',
      )?.href || ''
    );
  }
  return '';
});

const faq = computed(() => [
  {
    question: $gettext('What does the name RobCo stand for?'),
    answer: $gettext('Robot Company'),
    tags: [$gettext('Sales'), $gettext('Onboarding')],
  },
  {
    question: $gettext('What does RobCo do?'),
    answer: $gettext(
      'RobCo is a manufacturer of smart, modular robots. Our core products are robots (hardware), AI-based software and cloud - all from a single source. In addition, we supply complete robot cells for various applications within industrial automation. With several thousand modules, RobCo employees support their customers every day in rethinking automation and tackling pressing challenges such as the shortage of skilled workers, production scaling and the transformation to Industry 4.0.',
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding')],
  },
  {
    question: $gettext('What is the difference between a robot and a cobot?'),
    answer: $gettext(
      'In summary, the distinction between modular robots and cobots lies in their design philosophy and intended applications. Modular robots prioritize adaptability and scalability, allowing for custom automation solutions tailored to specific requirements. Meanwhile, cobots prioritize collaboration and safety, enabling human-robot interaction in shared workspaces to enhance productivity and efficiency. Important to know is that our modular robots offers the same safety specifications like cobots already.',
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding')],
  },
  {
    question: $gettext("What is RobCo's objective?"),
    answer: $gettext(
      "RobCo offers adaptable, affordable and connected robotic solutions for SMEs to automate repetitive manual tasks. These are based on a patented and world-first modular hardware kit and the RobCo Studio software platform. Whether machine tending, palletizing, dispensing or welding, RobCo's modular robots are versatile and intuitive. We help industrial companies to remain competitive, scale production and counteract the shortage of skilled workers.",
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding')],
  },
  {
    question: $gettext(
      'There are numerous robotics companies. What makes RobCo/its products unique?',
    ),
    answer: $gettext(
      'RobCo is unique because we develop smart modular robots to supercharge your factory and help companies take their performance to the next level. With our combination of hardware, software and implementation - all from a single source - we offer a complete automation solution that is easy and quick to deploy.<br/>Our AI-based software makes it possible to configure, implement and manage robots intuitively and efficiently via a digital twin. This ensures a plug-and-play application without complex programming or specialized personnel - and with ROI from day 1.<br/>Thanks to our patented modular hardware and the RobCo Studio software platform, our robots can be flexibly adapted to your individual requirements. Whether machine tending, palletizing, welding or dispensing - RobCo stands for flexibility, modularity and cost efficiency.',
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding')],
  },
  {
    question: $gettext('What applications are the robots suitable for?'),
    answer: $gettext(
      'Our robots are easy to customize and can be used for a wide range of automation applications — from manufacturing to the food and beverage industry.<br/>Our focus is on the industrial market. Common applications include machine tending (loading and unloading parts into CNC machines), palletizing, assembly, pick-and-place for inspections, packaging, part assembly, etc.',
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding')],
  },
  {
    question: $gettext(
      'Do you provide training for technicians to program the robot?',
    ),
    answer: $gettext(
      "We offer various training courses for the use and maintenance of our robots and systems.<br/>However, our robots don't need to be programmed. We offer no-code software to set up and change the robot task — an intuitive app is used to graphically configure a workflow and teach the robot the task. This takes between 10 and 30 minutes on average.",
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding')],
  },
  {
    question: $gettext('Where do you produce?'),
    answer: $gettext(
      'Our robots are designed, developed, produced and tested in Munich, Germany.',
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding')],
  },
  {
    question: $gettext(
      'Which parts of the product are produced in Asia/China?',
    ),
    answer: $gettext(
      'Our supply chain is in Europe, with a number of key parts being purchased from suppliers in Germany.',
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding')],
  },
  {
    question: $gettext('How can I try out the software?'),
    answer: $gettext(
      'You can try out the software in our Cloud Robflow instance in RobCo Studio. Each user in RobCo Studio gets its own virtual robot instance.<br/>In addition, you can find information and tutorial videos on Youtube or contact one of our colleagues for an individual demo.',
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding')],
  },
  {
    question: $gettext('Can we get a free demo robot?'),
    answer: $gettext(
      'RobCo does not provide free demo robots. However, we offer discounted prices for demo robots. We also offer a special mobile demo robot. Please ask your account manager for details and prices.',
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding')],
  },
  {
    question: $gettext('How much does a robot cost?'),
    answer: $gettext(
      'The price of our robots depends on the payload, reach, axes of movement, etc.<br/>Up to 60 different configurations are possible.<br/>The prices range from around € 10T to around € 80T. Some common configurations are listed in our price overview.<br/>To get the price for a customized configuration, please let us know your requirements.<br/>The price includes the robot arm, a control unit (CU) and the software.',
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding'), $gettext('Price')],
  },
  {
    question: $gettext('What are the additional costs?'),
    answer: $gettext(
      'Prices exclude grippers/end-of-arm tools, peripherals, safety cover, installation and service.',
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding'), $gettext('Price')],
  },
  {
    question: $gettext('What is the maximum load?'),
    answer: $gettext(
      'Currently, 40kg load capacity at 1.1m reach and 18kg load capacity at 2.1m reach. See <a href=%{link} target="_blank" rel="noopener noreferrer">data sheet</a>.',
      {
        link: technicalDataPdfLink.value,
      },
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding'), $gettext('Product')],
  },
  {
    question: $gettext('What is the protection class of the robots?'),
    answer: $gettext(
      'Currently, IP54. See IP rating in <a href=%{link} target="_blank" rel="noopener noreferrer">spec sheet</a>',
      { link: technicalDataPdfLink.value },
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding'), $gettext('Product')],
  },
  {
    question: $gettext('What is the precision/accuracy of the robots?'),
    answer: $gettext(
      'Currently, 0.1mm precision. See <a href=%{link} target="_blank" rel="noopener noreferrer">spec sheet</a>.',
      {
        link: technicalDataPdfLink.value,
      },
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding'), $gettext('Product')],
  },
  {
    question: $gettext(
      'How can the robots be integrated into an automation system?',
    ),
    answer: $gettext(
      'Various options via digital and serial interfaces. See Interfaces/Programming in <a href=%{link} target="_blank" rel="noopener noreferrer">spec sheet</a>.',
      {
        link: technicalDataPdfLink.value,
      },
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding'), $gettext('Product')],
  },
  {
    question: $gettext("How can I extend RobFlow's functionality?"),
    answer: $gettext(
      'You can directly communicate with the Robot via our HTTP API if you need to control it by yourself.',
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding'), $gettext('Product')],
  },
  {
    question: $gettext(
      'In which programming language are the robots configured?',
    ),
    answer: $gettext(
      'You can program your robot with our graphical interface called RobFlow. No programming skills are required for this at all.',
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding'), $gettext('Product')],
  },
  {
    question: $gettext('Can the robots be accessed remotely?'),
    answer: $gettext(
      'You can get insights on your robot fleet via RobCo Studio and also work on your programs on your own virtual robot instance there. However, directly controlling the robot remotely is not possible due to security reasons.',
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding'), $gettext('Product')],
  },
  {
    question: $gettext('Which grippers can be used with the robots?'),
    answer: $gettext(
      'Our robots have an ISO 50 interface that can be used with various grippers.<br/>We offer grippers of various types and brands, depending on the application.<br/>We also make custom grippers for specific projects.',
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding'), $gettext('Product')],
  },
  {
    question: $gettext('What does “teaching” mean?'),
    answer: $gettext(
      "We've made it easy to set up a workflow — no programming is required, just configuration.<br/>The user can create a workflow in an app with an intuitive graphical interface and instruct the robot arm to perform the movements. It's called teaching.",
    ),
    tags: [$gettext('Sales'), $gettext('Onboarding'), $gettext('Product')],
  },
]);
</script>
<template>
  <div class="pa-4 pa-sm-12">
    <v-row>
      <v-col class="mb-4">
        <h1>
          {{ $gettext('Frequently asked questions') }}
        </h1>
      </v-col>
    </v-row>
    <v-row>
      <v-expansion-panels :multiple="true">
        <v-col v-for="(entry, idx) in faq" :key="idx" cols="12" md="6">
          <v-expansion-panel
            :title="entry.question"
            style="border-radius: 16px"
          >
            <template #title>
              <h3 class="font-weight-medium" v-html="entry.question" />
              <v-spacer />
              <span>
                <v-chip
                  v-for="tag in entry.tags"
                  :key="tag"
                  size="x-small"
                  :label="true"
                  class="ma-1 float-end cursor-pointer"
                >
                  {{ tag.toUpperCase() }}
                </v-chip>
              </span>
            </template>
            <template #text>
              <p v-html="entry.answer" />
            </template>
          </v-expansion-panel>
        </v-col>
      </v-expansion-panels>
    </v-row>
    <v-row></v-row>
  </div>
</template>
<style lang="scss" scoped></style>
